import {GROUND_BACKGROUNDS} from '../groundBackgrounds';

/**
 * ПЕСЧАНЫЕ ЗМЕЙКИ — эффект пустынь (desert_1, desert_2).
 *
 * Каждые девять–пятнадцать секунд по песчаному морю проходит порыв: полосой в
 * несколько ячеек бегут золотистые извилистые ленты песка, который ветер несет
 * над самой землей (см. utils/effects/biome/sandStreamers). Лента не летит по
 * прямой, а течет по извилистому руслу — будто обтекает барханы.
 *
 * Песок на песке не видно: тонкие ленты песочного цвета терялись в ряби
 * барханов даже на вдвое увеличенном макете. Поэтому ленты широкие и светятся
 * (сложение цветов — блик солнца на летящем песке), а идут порывами: глаз
 * ловит согласованное движение. Лежат ленты на самой земле: жетоны и отряды
 * их закрывают.
 */
export const SAND_STREAMERS_KIND = 'sandStreamers';

const SAND_STREAMERS = Object.freeze({
  // Первый порыв — вскоре после начала, следующий — через паузу от начала
  // предыдущего. Через песчаное море порыв идет 8–16 секунд, столько же,
  // сколько длится пауза, а двух порывов разом не бывает: новый приходит сразу
  // за ушедшим или после нескольких секунд тишины
  firstDelayMs: {min: 2500, max: 6000},
  intervalMs: {min: 9000, max: 15000},
  // Скорость порыва, размеров ячейки в секунду
  speedFactor: {min: 2.8, max: 4},
  // У кромки песчаного моря ленты гаснут: спад занимает последнюю такую долю
  // его полуосей. Дальше — скалы, оазисы и море, песку там взяться неоткуда
  plainFade: 0.2,
  // С каким шагом (в размерах ячейки) при рождении порыва ищется, где русло
  // каждой ленты входит на песок и где уходит с него
  probeStepFactor: 0.5,
  // Золотистый блик: прибавляется к цвету песка
  tint: 0xffcf82,
  streamers: {
    // Лент в порыве; потолок — 8 лент по 10 штрихов, 80 спрайтов
    count: {min: 6, max: 8},
    // Лента собрана из мягких штрихов, уложенных вдоль русла внахлест
    segmentCount: 10,
    // Кадр штриха длиннее шага между штрихами во столько раз: при таком
    // нахлесте яркость вдоль ленты ровная, без «бусин» (и в 1.6 раза выше
    // яркости одного штриха — это учтено в alpha подложек)
    segmentOverlap: 2.6,
    // Расстояние между соседними лентами поперек пути, размеров ячейки:
    // порыв выходит полосой в 4–6 ячеек
    laneGapFactor: {min: 1.1, max: 1.8},
    // На сколько лента отстает от фронта порыва, размеров ячейки: ленты идут
    // не шеренгой
    lagFactor: {min: 0, max: 6},
    // Длина ленты, размеров ячейки. Короче двух ячеек (4 размеров) лента
    // пропадает в ряби барханов
    lengthFactor: {min: 4, max: 7.2},
    // Высота кадра штриха у головы ленты, размеров ячейки; видимый гребень
    // втрое уже. Тоньше лента не читается ни при какой яркости. К хвосту
    // лента сужается до такой доли толщины
    thicknessFactor: {min: 0.36, max: 0.5},
    tailThicknessShare: 0.55,
    // Русло: волна поперек пути, неподвижная в пространстве, — лента по ней
    // скользит. Размах и длина волны — в размерах ячейки
    waveAmplitudeFactor: {min: 0.2, max: 0.44},
    waveLengthFactor: {min: 3.2, max: 5.2},
    // Лента чуть быстрее или медленнее порыва
    speedShare: {min: 0.94, max: 1.06},
    // Одни ленты ярче, другие бледнее (доля яркости подложки)
    alphaShare: {min: 0.75, max: 1},
    // Яркость вдоль ленты: к обоим концам сходит на нет (степень синуса),
    // хвост бледнее головы (доля яркости у хвоста)
    taperPower: 0.7,
    tailShare: 0.55,
    // Лента дышит: то разгорается, то бледнеет на такую долю яркости
    pulseDepth: 0.3,
    pulsePeriodMs: {min: 2600, max: 4400},
  },
});

/**
 * По подложкам:
 *  - headingDeg — куда несет песок: наклон пути вниз от горизонтали. Сторона
 *    (вправо или влево) — по ветру матча, тому же, что несет облака;
 *  - alpha — яркость одного штриха ленты. Лента из штрихов внахлест в самом
 *    ярком своем месте прибавляет к песку около alpha * 1.3 (нахлест 1.6 на
 *    пик яркости вдоль ленты 0.8): больше 0.45 — и на светлых гранях барханов
 *    блик выгорает в неоновый желтый, меньше 0.3 — теряется в их ряби;
 *  - plain — песчаное море на картинке пейзажа (доли ее стороны).
 */
export const SAND_STREAMERS_EFFECT = Object.freeze({
  kind: SAND_STREAMERS_KIND,
  backgrounds: Object.freeze({
    // Гребни барханов нарисованы почти горизонтально: ленты идут наискось,
    // поперек гребней
    [GROUND_BACKGROUNDS.DESERT_1]: Object.freeze({
      ...SAND_STREAMERS,
      headingDeg: 35,
      alpha: 0.33,
      plain: {u: 0.5, v: 0.48, radiusU: 0.27, radiusV: 0.3},
    }),
    // Песок гладкий, с нарисованными почти горизонтальными пыльными разводами:
    // ленты идут вдоль них, и яркости нужно меньше
    [GROUND_BACKGROUNDS.DESERT_2]: Object.freeze({
      ...SAND_STREAMERS,
      headingDeg: 12,
      alpha: 0.29,
      plain: {u: 0.47, v: 0.5, radiusU: 0.27, radiusV: 0.29},
    }),
  }),
});
